'use client';
import { forwardRef, useState, useRef, useEffect } from 'react';
import { IconCheckThick } from '@wanteddev/wds-icon';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { useSize } from '@radix-ui/react-use-size';
import { usePrevious } from '@radix-ui/react-use-previous';
import { Box } from '@wanteddev/wds-engine';
import WithInteraction from '../with-interaction/index.mjs';
import { checkboxStyle } from './style.mjs';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';

var Checkbox = forwardRef(
  ({
    name,
    defaultChecked = false,
    icon: originIcon,
    disabled,
    required,
    checked: originChecked,
    onCheckedChange,
    size = "small",
    invalid = false,
    bold,
    xs,
    sm,
    md,
    lg,
    xl,
    ...props
  }, ref) => {
    const icon = originIcon || /* @__PURE__ */ jsx(IconCheckThick, {});
    const [button, setButton] = useState(null);
    const composedRefs = useComposedRefs(ref, (node) => setButton(node));
    const hasConsumerStoppedPropagationRef = useRef(false);
    const isFormControl = button ? Boolean(button.closest("form")) : true;
    const [checked = false, setChecked] = useControllableState({
      prop: originChecked,
      defaultProp: defaultChecked,
      onChange: onCheckedChange
    });
    const initialCheckedStateRef = useRef(checked);
    useEffect(() => {
      const form = button?.form;
      if (form) {
        const reset = () => setChecked(initialCheckedStateRef.current);
        form.addEventListener("reset", reset);
        return () => form.removeEventListener("reset", reset);
      }
    }, [button, setChecked]);
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      isFormControl && /* @__PURE__ */ jsx(
        BubbleInput,
        {
          name,
          value: checked ? "on" : "off",
          type: "checkbox",
          checked,
          defaultChecked,
          sx: { display: "none", pointerEvents: "none" },
          control: button,
          bubbles: !hasConsumerStoppedPropagationRef.current
        }
      ),
      /* @__PURE__ */ jsx(
        WithInteraction,
        {
          width: "calc(100% + 8px)",
          height: "calc(100% + 8px)",
          disabled,
          scale: true,
          children: /* @__PURE__ */ jsx(
            Box,
            {
              as: "button",
              type: "button",
              role: "checkbox",
              "aria-checked": checked,
              "aria-disabled": disabled,
              "aria-invalid": invalid,
              disabled,
              "aria-required": required,
              ref: composedRefs,
              ...props,
              sx: [
                checkboxStyle({
                  size,
                  checked,
                  disabled,
                  bold,
                  // invalid,
                  xs,
                  sm,
                  md,
                  lg,
                  xl
                }),
                props.sx
              ],
              onKeyDown: composeEventHandlers(props.onKeyDown, (event) => {
                if (event.key === "Enter")
                  event.preventDefault();
              }),
              onClick: composeEventHandlers(props.onClick, (event) => {
                setChecked((prevChecked) => !prevChecked);
                if (isFormControl) {
                  hasConsumerStoppedPropagationRef.current = event.isPropagationStopped();
                  if (!hasConsumerStoppedPropagationRef.current)
                    event.stopPropagation();
                }
              }),
              children: /* @__PURE__ */ jsx("span", { children: icon })
            }
          )
        }
      )
    ] });
  }
);
Checkbox.displayName = "Checkbox";
var checkbox_default = Checkbox;
var BubbleInput = ({
  control,
  checked,
  bubbles = true,
  ...inputProps
}) => {
  const ref = useRef(null);
  const prevChecked = usePrevious(checked);
  const controlSize = useSize(control);
  useEffect(() => {
    const input = ref.current;
    const inputProto = window.HTMLInputElement.prototype;
    const descriptor = Object.getOwnPropertyDescriptor(
      inputProto,
      "checked"
    );
    const setChecked = descriptor.set;
    if (prevChecked !== checked && setChecked) {
      const event = new Event("click", { bubbles });
      setChecked.call(input, checked);
      input.dispatchEvent(event);
    }
  }, [prevChecked, checked, bubbles]);
  return /* @__PURE__ */ jsx(
    Box,
    {
      as: "input",
      type: "checkbox",
      "aria-hidden": true,
      defaultChecked: checked,
      ...inputProps,
      tabIndex: -1,
      ref,
      sx: [
        {
          ...controlSize,
          transform: "translateX(-100%)",
          position: "absolute",
          pointerEvents: "none",
          opacity: 0,
          margin: 0
        },
        inputProps.sx
      ]
    }
  );
};

export { checkbox_default as default };
